
import { useState } from "react";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import emailjs from '@emailjs/browser';
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export const PlanningForm = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [subject, setSubject] = useState("");
  const [amount, setAmount] = useState("");
  const [timelineOption, setTimelineOption] = useState("");
  const [songs, setSongs] = useState(["", "", "", ""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSongChange = (index: number, value: string) => {
    const newSongs = [...songs];
    newSongs[index] = value;
    setSongs(newSongs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data: secrets, error: secretsError } = await supabase.functions.invoke('get-emailjs-config');
      
      if (secretsError) {
        console.error('Error fetching EmailJS config:', secretsError);
        throw secretsError;
      }
      
      if (!secrets?.serviceId || !secrets?.templateId || !secrets?.publicKey) {
        console.error('EmailJS configuration is incomplete:', secrets);
        throw new Error('EmailJS configuration is incomplete');
      }

      const formDetails = `
Name: ${name}
Email: ${email}
Verification Code: ${code}
Subject of Montage: ${subject}
Amount Paid: ${amount}
Timeline Option: ${timelineOption}
Songs:
1. ${songs[0]}
2. ${songs[1]}
3. ${songs[2]}
4. ${songs[3]}`;

      const response = await emailjs.send(secrets.serviceId, secrets.templateId, {
        to_email: "mymontage.video@gmail.com",
        from_name: name,
        from_email: email,
        subject: "New Planning Form Submission",
        message: formDetails
      }, secrets.publicKey);

      console.log('EmailJS response:', response);

      if (response.status === 200) {
        toast({
          title: "Form Submitted Successfully!",
          description: "We'll review your planning details and get back to you soon.",
          duration: 5000,
        });

        // Reset form
        setEmail("");
        setName("");
        setCode("");
        setSubject("");
        setAmount("");
        setTimelineOption("");
        setSongs(["", "", "", ""]);
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      toast({
        title: "Error Submitting Form",
        description: error?.message || "There was an error submitting your form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-lg"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-navy-800">Planning Form</h2>
        <Button 
          variant="outline"
          onClick={() => navigate("/")}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="name">What is your full name? *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="code">What is the code that we sent to you in the welcome email? *</Label>
          <Input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="subject">What is the name of the subject of the montage? *</Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="amount">Please type the full amount that was sent in the email that gave you this link. *</Label>
          <Input
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>

        <div>
          <Label>Pick one of the timeline options for the montage. *</Label>
          <RadioGroup value={timelineOption} onValueChange={setTimelineOption} required>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="chronological" id="chronological" />
              <Label htmlFor="chronological">Chronological (In order of the subject's life, you need to sort photos in this order)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="category" id="category" />
              <Label htmlFor="category">Category (Video goes by categories like; baseball, tennis, acting, family)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="chronological-category" id="chronological-category" />
              <Label htmlFor="chronological-category">Chronological Category (Video has categories but is in chronological order inside each category)</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <div className="flex items-center gap-4 mb-2">
            <Label>Songs (4 required) *</Label>
            <a 
              href="https://open.spotify.com/playlist/012dbwu4kb6PiUC0lrP5iR?si=6d777d3d5e8a4a55&nd=1&dlsi=8e3cd24c6c5e42b5"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy-600 hover:text-navy-800 underline"
            >
              Need inspiration? Click Here
            </a>
          </div>
          {songs.map((song, index) => (
            <Input
              key={index}
              value={song}
              onChange={(e) => handleSongChange(index, e.target.value)}
              placeholder={`Song #${index + 1}`}
              className="mt-2"
              required
            />
          ))}
        </div>

        <Button
          type="submit"
          className="w-full bg-navy-600 text-white"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Form"}
        </Button>
      </form>
    </motion.div>
  );
};
